import { useState } from "react";
import { IQuotaAlertState } from "../../../../App/Typings/State/IQuotaAlertState";
import { useSelectedClient } from "../../../../App/Queries/ClientQueries";

enum QuotaThreshold {
  None = "None",
  ApproachingLimit = "ApproachingLimit",
  HighQuotaUsage = "HighQuotaUsage",
  QuotaExceeded = "QuotaExceeded",
}

export const ApproachingLimitRatio = 0.8;
export const HighQuotaUsageRatio = 0.9;
export const QuotaExceededRatio = 1;

export const useQuotaAlertsState = () => {
  const [hasUserClosedEmailAlert, setHasUserClosedEmailAlert] = useState(false);
  const [hasUserClosedTextAlert, setHasUserClosedTextAlert] = useState(false);
  const { data: selectedClient } = useSelectedClient();

  const emailQuotaFromApi = selectedClient?.quotas?.emailQuota || 0;
  const sentEmailCountFromApi = selectedClient?.sentEmailCount || 0;
  const sentTextCountFromApi = selectedClient?.sentTextCount || 0;
  const textQuotaFromApi = selectedClient?.quotas?.textQuota || 0;

  const emailThresholdHit = getQuotaThreshold(emailQuotaFromApi, sentEmailCountFromApi);
  const textThresholdHit = getQuotaThreshold(textQuotaFromApi, sentTextCountFromApi);
  const emailRemainingThreshold = getRemainingThreshold(emailThresholdHit, emailQuotaFromApi);
  const textRemainingThreshold = getRemainingThreshold(textThresholdHit, textQuotaFromApi);

  //default to false. If a client doesn't have a quota set up yet, we probably shouldn't show the messages?
  let shouldShowEmailError,
    shouldShowEmailWarning,
    shouldShowTextError,
    shouldShowTextWarning = false;

  //if the client doesn't have an email quota set up, we shouldn't alert against it
  if (emailQuotaFromApi > 0 && !hasUserClosedEmailAlert) {
    shouldShowEmailError = emailThresholdHit === QuotaThreshold.QuotaExceeded;
    shouldShowEmailWarning =
      !shouldShowEmailError &&
      (emailThresholdHit === QuotaThreshold.ApproachingLimit ||
        emailThresholdHit === QuotaThreshold.HighQuotaUsage);
  }

  //if the client doesn't have an text message quota set up, we shouldn't alert against it
  if (textQuotaFromApi > 0 && !hasUserClosedTextAlert) {
    shouldShowTextError = textThresholdHit === QuotaThreshold.QuotaExceeded;
    shouldShowTextWarning =
      !shouldShowTextError &&
      (textThresholdHit === QuotaThreshold.HighQuotaUsage ||
        textThresholdHit === QuotaThreshold.ApproachingLimit);
  }

  return {
    hasUserClosedEmailAlert,
    hasUserClosedTextAlert,
    shouldShowEmailError,
    shouldShowEmailWarning,
    shouldShowTextError,
    shouldShowTextWarning,
    emailRemainingThreshold,
    textRemainingThreshold,
    setHasUserClosedEmailAlert,
    setHasUserClosedTextAlert,
  } as IQuotaAlertState;
};

function getQuotaThreshold(quota: number, sentCount: number): QuotaThreshold {
  const usage = quota > 0 ? sentCount / quota : 0;
  switch (true) {
    case usage >= QuotaExceededRatio:
      return QuotaThreshold.QuotaExceeded;
    case usage >= HighQuotaUsageRatio:
      return QuotaThreshold.HighQuotaUsage;
    case usage >= ApproachingLimitRatio:
      return QuotaThreshold.ApproachingLimit;
    default:
      return QuotaThreshold.None;
  }
}

function getRemainingThreshold(threshold: QuotaThreshold, quota: number): number {
  switch (threshold) {
    case QuotaThreshold.None:
      return quota;
    case QuotaThreshold.ApproachingLimit:
      return Math.ceil(quota - ApproachingLimitRatio * quota);
    case QuotaThreshold.HighQuotaUsage:
      return Math.ceil(quota - HighQuotaUsageRatio * quota);
    case QuotaThreshold.QuotaExceeded:
      return 0;
  }
}
